/**
 * Google Enhanced Conversions — user-data push.
 *
 * Hashes PII with SHA-256 (per Google spec) and forwards it to:
 *   - gtag('set', 'user_data', ...)  — picked up by GA4 & Google Ads tags
 *   - dataLayer 'user_data' event    — picked up by GTM Enhanced Conversions tag
 *
 * Google requires email/phone hashed. Names/address can also be hashed.
 * Phone must be E.164 (+15551234567). Email lowercased + trimmed.
 *
 * Safe to call before tags load — gtag queues, dataLayer is just an array.
 */

const normalizeEmail = (email: string): string => email.trim().toLowerCase();

const normalizePhone = (phone: string, defaultCountry = '1'): string => {
  const digits = phone.replace(/\D/g, '');
  if (!digits) return '';
  // If no country code present (US default), prepend it
  if (digits.length === 10) return `+${defaultCountry}${digits}`;
  if (digits.length === 11 && digits.startsWith(defaultCountry)) return `+${digits}`;
  return `+${digits}`;
};

const normalizeName = (name: string): string =>
  name.trim().toLowerCase().replace(/[^a-z\s'-]/g, '');

async function sha256Hex(input: string): Promise<string> {
  if (!input) return '';
  if (typeof crypto === 'undefined' || !crypto.subtle) return '';
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(input));
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export interface EnhancedConversionsInput {
  email?: string | null;
  phone?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  country?: string | null; // 2-letter ISO, defaults to 'US'
}

/**
 * Hash + push user-provided data for Google Enhanced Conversions.
 * No-op if no email or phone is provided.
 */
export async function pushEnhancedConversionsUserData(
  input: EnhancedConversionsInput
): Promise<void> {
  try {
    const email = input.email ? normalizeEmail(input.email) : '';
    const phone = input.phone ? normalizePhone(input.phone) : '';
    if (!email && !phone) return;

    const firstName = input.firstName ? normalizeName(input.firstName) : '';
    const lastName = input.lastName ? normalizeName(input.lastName) : '';
    const city = input.city ? normalizeName(input.city).replace(/\s+/g, '') : '';
    const state = input.state ? input.state.trim().toLowerCase() : '';
    const zip = input.zip ? input.zip.trim().toLowerCase() : '';
    const country = (input.country || 'us').trim().toLowerCase();

    const [sha_email, sha_phone, sha_first, sha_last] = await Promise.all([
      sha256Hex(email),
      sha256Hex(phone),
      sha256Hex(firstName),
      sha256Hex(lastName),
    ]);

    // gtag.js Enhanced Conversions — set on the page; Google Ads/GA4 tags read it.
    const userData: Record<string, unknown> = {};
    if (sha_email) userData.sha256_email_address = sha_email;
    if (sha_phone) userData.sha256_phone_number = sha_phone;
    const address: Record<string, unknown> = {};
    if (sha_first) address.sha256_first_name = sha_first;
    if (sha_last) address.sha256_last_name = sha_last;
    if (city) address.city = city;
    if (state) address.region = state;
    if (zip) address.postal_code = zip;
    if (country) address.country = country;
    if (Object.keys(address).length) userData.address = address;

    const w = window as any;
    if (typeof w.gtag === 'function') {
      w.gtag('set', 'user_data', userData);
    }

    // GTM Enhanced Conversions — variable readable via dataLayer 'enhanced_conversion_data'.
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push({
      event: 'user_data_ready',
      enhanced_conversion_data: userData,
    });
  } catch {
    // Never break form submission because of analytics
  }
}
